
import {
  computed, defineComponent, reactive, ref,
} from 'vue';
import { IOption } from '@/core/models/AntDesign/IOption';
import { IParametrosConsultaRapida } from '@/core/models/Consulta/IParametrosConsultaRapida';
import { useTelaBase } from '@/core/composables/TelaBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import ServicoUnidade from '@/servicos/Cadastros/Produtos/ServicoUnidade';
import storeSistema from '@/store/storeSistema';

export default defineComponent({
  name: 'SelecionarUnidade',
  props: {
    varios: {
      type: Boolean,
      default: false,
    },
    codigoSelecionado: {
      type: Number || undefined,
    },
    codigosSelecionados: {
      type: Array as () => number[],
      default: () => [],
    },
    descricaoSelecionado: {
      type: String,
      default: '',
    },
    siglaSelecionada: {
      type: String,
      default: '',
    },
    empresas: {
      type: Array as () => number[],
      default: () => [],
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    classCss: {
      type: String,
      default: '',
    },
    ignorarUnidades: {
      type: Array as () => number[],
    },
    apresentarApenasSigla: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:codigoSelecionado', 'update:codigosSelecionados', 'update:descricaoSelecionado', 'update:siglaSelecionada', 'change', 'blur'],
  setup(props, { emit }) {
    const { montaOpcoesComListaConsultaRapida } = useTelaBase();
    const refSelecionarUnidade = ref<HTMLElement | null>(null);

    let debounce = 0;
    const servicoUnidade = new ServicoUnidade();

    const state = reactive({
      listaUnidades: [] as IOption[],
      buscandoDados: false,
      emDigitacao: false,
    });

    const computedDescricaoSelecionado = computed({
      get: () => props.descricaoSelecionado,
      set: (val: string) => {
        emit('update:descricaoSelecionado', val);
      },
    });

    const computedSiglaSelecionada = computed({
      get: () => props.siglaSelecionada,
      set: (val: string) => {
        emit('update:siglaSelecionada', val);
      },
    });

    async function obterUnidadePorCodigo(valor: any) {
      if (state.emDigitacao) { return; }
      if (!UtilitarioGeral.valorValido(valor)) { return; }
      if (!(valor > 0)) { return; }

      if (props.varios) {
        if (props.codigosSelecionados.length === 0) { return; }
      } else if (props.codigoSelecionado === 0) {
        return;
      }
      let efetuarConsulta = true;

      if (UtilitarioGeral.validaLista(state.listaUnidades)) {
        if (props.varios) {
          const listaCodigosConsulta = state.listaUnidades.map((c) => c.value);
          const diferencas = props.codigosSelecionados.filter((c) => !listaCodigosConsulta.includes(c));
          if (!UtilitarioGeral.validaLista(diferencas)) {
            efetuarConsulta = false;
          }
        } else {
          const existeLista = state.listaUnidades.find((c) => c.value === props.codigoSelecionado);
          if (existeLista !== undefined) {
            efetuarConsulta = false;
          }
        }

        const option = state.listaUnidades.find((c) => c.value === valor);
        if (option?.label !== undefined && option?.label !== '') {
          computedDescricaoSelecionado.value = option?.label;
          const sigla = option?.label.split('-')[0].trim();
          computedSiglaSelecionada.value = sigla;
        }
      }

      if (!efetuarConsulta) return;

      state.listaUnidades = [];

      const parametrosConsultaRapida: IParametrosConsultaRapida = {
        apenasAtivos: false, recursoAssociado: '',
      };

      if (UtilitarioGeral.validaLista(props.empresas)) {
        parametrosConsultaRapida.empresas = props.empresas;
      } else {
        parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
      }

      if (props.varios) {
        parametrosConsultaRapida.filtrarPorVariosCodigos = true;
        parametrosConsultaRapida.valores = valor;
      } else {
        parametrosConsultaRapida.filtrarPorCodigo = true;
        parametrosConsultaRapida.valor = valor;
      }

      const listaUnidades = await servicoUnidade.consultaRapida(parametrosConsultaRapida, props.apresentarApenasSigla);
      state.listaUnidades = montaOpcoesComListaConsultaRapida(listaUnidades);
      if (!UtilitarioGeral.validaLista(listaUnidades)) {
        if (props.varios) {
          emit('update:codigosSelecionados', undefined);
        } else {
          emit('update:codigoSelecionado', undefined);
        }
      }
    }

    async function pesquisarUnidade(valorPesquisa: any) {
      state.emDigitacao = true;

      state.buscandoDados = true;
      state.listaUnidades = [];
      clearTimeout(debounce);
      debounce = setTimeout(async () => {
        const parametrosConsultaRapida: IParametrosConsultaRapida = {
          valor: valorPesquisa, apenasAtivos: true, recursoAssociado: '', quantidadeRegistros: 100,
        };

        if (UtilitarioGeral.validaLista(props.empresas)) {
          parametrosConsultaRapida.empresas = props.empresas;
        } else {
          parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
        }

        let listaUnidades = await servicoUnidade.consultaRapida(parametrosConsultaRapida, props.apresentarApenasSigla);
        if (props.ignorarUnidades !== undefined) {
          props.ignorarUnidades.forEach((codigoUnidade) => {
            listaUnidades = listaUnidades.filter((c) => c.codigo !== codigoUnidade);
          });
        }
        state.buscandoDados = false;
        state.listaUnidades = montaOpcoesComListaConsultaRapida(listaUnidades);
      }, 600);
    }

    function obterUnidade():any {
      obterUnidadePorCodigo(props.codigoSelecionado);
      return (props.codigoSelecionado === 0 ? undefined : props.codigoSelecionado);
    }

    const computedCodigoSelecionado = computed({
      get: () => obterUnidade(),
      set: (val: number) => {
        emit('update:codigoSelecionado', val);
        const option = state.listaUnidades.find((c) => c.value === val);
        if (option?.label !== undefined && option?.label !== '') {
          computedDescricaoSelecionado.value = option?.label;
          const sigla = option?.label.split('-')[0].trim();
          computedSiglaSelecionada.value = sigla;
        }
      },
    });

    function obterUnidades():number[] {
      obterUnidadePorCodigo(props.codigosSelecionados);
      return props.codigosSelecionados;
    }

    const computedCodigosSelecionados = computed({
      get: () => obterUnidades(),
      set: (val: number[]) => {
        emit('update:codigosSelecionados', val);
      },
    });

    function change() {
      emit('change', props.varios ? props.codigosSelecionados : props.codigoSelecionado);
    }

    function focus() {
      if (refSelecionarUnidade.value) {
        refSelecionarUnidade.value.focus();
      }
    }

    function blur() {
      state.emDigitacao = false;
      emit('blur');
    }

    return {
      props,
      state,
      refSelecionarUnidade,
      pesquisarUnidade,
      computedCodigoSelecionado,
      computedCodigosSelecionados,
      computedDescricaoSelecionado,
      computedSiglaSelecionada,
      change,
      focus,
      blur,
    };
  },
});
